import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form } from '@unform/web';
import { BiX } from 'react-icons/bi';
import { FormHandles } from '@unform/core';

import { ValidationError } from 'yup';
import { IoIosWarning } from 'react-icons/io';
import Tab from '../../../../newComponents/Tab';
import Button from '../../../../newComponents/Button';
import TabBar from '../../../../newComponents/TabBar';
import { ModalHandles } from '../../../../newComponents/Modal';

import Sizes, { SizesFormSchema } from './steps/Sizes';
import Details, { DetailsFormSchema } from './steps/Details';
import Complements, { ComplementsFormSchema } from './steps/Complements';

import { useAuth } from '../../../../hooks/auth';
import { useToast } from '../../../../hooks/toast';
import { usePizzaGroup } from '../../../../hooks/pizzaGroup';

import GroupsService, { Group } from '../../../../services/GroupsService';
import SizesService from '../../../../services/SizesService';

import { ISize } from '../../../../models';
import APIError from '../../../../errors/APIError';
import getValidationErrors from '../../../../utils/getValidationErrors';

import { Container, TabPanel, Modal, ModalHeader } from './styles';

type PizzaGroupProps = {
  lastGroupIndex: number;
  onSuccessSave(group: Group): void;
};

const LAST_TAB_INDEX = 2;

const SCHEMAS = {
  0: DetailsFormSchema,
  1: SizesFormSchema,
  2: ComplementsFormSchema,
};

const PizzaGroup: React.FC<PizzaGroupProps> = ({
  lastGroupIndex,
  onSuccessSave,
}) => {
  const formRef = useRef<FormHandles>(null);
  const modalRef = useRef<ModalHandles>(null);

  const {
    id,
    name,
    image,
    complements,
    currentTab,
    isDrawerOpened,
    isComplementsDrawerOpened,
    handleChangeTab,
    handleCloseDrawer,
    handleResetGroup,
  } = usePizzaGroup();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [sizes, setSizes] = useState<ISize[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleCancel = useCallback(() => {
    modalRef.current?.close();
    handleCloseDrawer();

    setTimeout(() => {
      handleChangeTab(0);
      handleResetGroup();
      formRef.current?.reset();
      formRef.current?.setErrors({});
    }, 500);
  }, [handleCloseDrawer, handleChangeTab, handleResetGroup]);

  const handleTryCancel = useCallback(() => {
    modalRef.current?.open();
  }, []);

  const handleSubmit = useCallback(
    async (data) => {
      if (isComplementsDrawerOpened) return;

      setIsLoading(true);

      formRef.current?.setErrors({});

      try {
        const currentTabSchema = SCHEMAS[currentTab];

        await currentTabSchema.validate(data, {
          abortEarly: false,
        });

        if (currentTab === LAST_TAB_INDEX || id) {
          await Promise.all(
            Object.values(SCHEMAS).map(async (schema) => {
              await schema.validate(data, {
                abortEarly: false,
              });
            }),
          );

          const formData = new FormData();

          if (image instanceof File) {
            formData.append('image', image.name);
            formData.append('images', image);
          } else if (typeof image === 'string') {
            formData.append('image', image);
          }

          const parsedComplements = (complements || []).map((complement) => ({
            ...complement,
            id: typeof complement.id === 'number' ? complement.id : undefined,
            items: (complement.items || []).map((item) => {
              const commomItem = {
                ...item,
                id: typeof item.id === 'number' ? item.id : undefined,
                image: undefined,
                price: {
                  value: item.price,
                },
                rawMaterialId:
                  typeof item.rawMaterialId === 'number'
                    ? item.rawMaterialId || undefined
                    : undefined,
              };

              if (item?.image instanceof File) {
                formData.append('images', item.image);

                return {
                  ...commomItem,
                  image: item.image.name,
                };
              }

              return commomItem;
            }),
          }));

          formData.append('name', data.name);
          formData.append('type', '3');
          formData.append('complements', JSON.stringify(parsedComplements));

          let group;

          if (id) {
            group = await GroupsService.editGroup(
              user.empresa_id,
              id,
              formData,
            );
          } else {
            formData.append('order', String(lastGroupIndex + 1));
            group = await GroupsService.createGroup(user.empresa_id, formData);
          }

          onSuccessSave({
            id: group.id,
            name: group.name,
            order: Number(group.order),
            type: Number(group.type),
            pauseSale: Boolean(group.pauseSale),
            products: [],
          });

          handleCancel();

          return;
        }

        handleChangeTab(currentTab + 1);
      } catch (error) {
        let errors = {};

        if (error instanceof ValidationError) {
          errors = getValidationErrors(error);

          const detailsTabFields = ['name'];

          if (
            Object.keys(errors).some((field) =>
              detailsTabFields.includes(field),
            )
          ) {
            handleChangeTab(0);
          }

          formRef.current?.setErrors(errors);
        }

        if (error instanceof APIError) {
          addToast({
            title: error.message,
            type: 'error',
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      id,
      image,
      currentTab,
      complements,
      user.empresa_id,
      isComplementsDrawerOpened,
      lastGroupIndex,
      addToast,
      handleCancel,
      onSuccessSave,
      handleChangeTab,
    ],
  );

  const handleLoadSizes = useCallback(async () => {
    try {
      const sizesList = await SizesService.listSizes(user.empresa_id, 3);

      setSizes(sizesList);
    } catch (error) {
      if (error instanceof APIError) {
        addToast({
          title: error.message,
          type: 'error',
        });
      }
    }
  }, [user.empresa_id, addToast]);

  useEffect(() => {
    handleLoadSizes();
  }, [handleLoadSizes]);

  useEffect(() => {
    formRef.current?.setFieldValue('name', name);
  }, [name]);

  return (
    <Container isVisible={isDrawerOpened}>
      <Form ref={formRef} onSubmit={handleSubmit} initialData={{ name }}>
        <header>
          <div className="drawer-title">
            <h1>{id ? 'Editar' : 'Novo'} grupo</h1>
            <button
              type="button"
              onClick={handleTryCancel}
              disabled={isLoading}
            >
              <BiX />
            </button>
          </div>
          <h3>
            {id ? (
              <>
                Você está editando os dados do grupo <strong>{name}</strong>
              </>
            ) : (
              'Defina os tamanhos e os complementos'
            )}
          </h3>
          <TabBar
            selectedTab={currentTab}
            onChange={(_event, value) => handleChangeTab(value)}
          >
            <Tab wrapped disabled={isLoading} style={{ fontSize: '14px' }}>
              Detalhes
            </Tab>
            <Tab wrapped disabled={isLoading} style={{ fontSize: '14px' }}>
              Tamanhos
            </Tab>
            <Tab wrapped disabled={isLoading} style={{ fontSize: '14px' }}>
              Complementos
            </Tab>
          </TabBar>
        </header>
        <main>
          <TabPanel index={0} selectedTab={currentTab}>
            <Details />
          </TabPanel>
          <TabPanel index={1} selectedTab={currentTab}>
            <Sizes sizes={sizes} />
          </TabPanel>
          <TabPanel index={2} selectedTab={currentTab}>
            <Complements />
          </TabPanel>
        </main>
        <footer>
          <Button
            type="button"
            variant="outlined"
            disabled={isLoading}
            onClick={handleTryCancel}
          >
            Cancelar
          </Button>
          <Button type="submit" loading={isLoading}>
            {currentTab === LAST_TAB_INDEX || id ? 'Salvar' : 'Continuar'}
          </Button>
        </footer>
      </Form>
      <Modal ref={modalRef} containerStyle={{ zIndex: 99999999 }}>
        <ModalHeader>
          <div>
            <IoIosWarning />
          </div>
          <h1>Atenção</h1>
          <button type="button" onClick={() => modalRef.current?.close()}>
            <BiX />
          </button>
        </ModalHeader>
        <p>
          Você não salvou suas alterações. Ao realizar a ação todos os dados
          serão perdidos.
          <br />
          <strong>Deseja prosseguir?</strong>
        </p>
        <Button full onClick={handleCancel}>
          Confirmar
        </Button>
        <Button
          full
          variant="outlined"
          onClick={() => modalRef.current?.close()}
        >
          Cancelar
        </Button>
      </Modal>
    </Container>
  );
};

export default PizzaGroup;
